<script setup>

import { markRaw, ref } from 'vue';
import { object, string } from 'yup';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { isEmpty } from 'ramda';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import useVerifyKey from '@/services/redeemKeys';
import WithLoader from '@/components/WithLoader.vue';

const discountModel = defineModel();

const { t } = useI18n();

const validationSchema = markRaw(object({
  discountCode: string().required().label(''),
}));

const { defineField, handleSubmit } = useForm({
  validationSchema,
});
const [discountCode, discountCodeAttrs] = defineField('discountCode');
const userMessage = ref('');

const {
  fetch, response, isLoading: isVerifyingKey, userFriendlyError,
} = useVerifyKey();

const doSubmitCode = handleSubmit(async (values) => {
  await fetch(values.discountCode);

  discountModel.value = {
    key: response.value.key,
    type: response.value.type,
    discount: response.value.discount,
    extraSeats: response.value.extraSeats,
  };

  if (isEmpty(userFriendlyError.value)) {
    userMessage.value = '';
  } else {
    discountModel.value = {};
    userMessage.value = t('registerView.billing.invalidKey');
  }
});

const clearDiscount = () => {
  discountModel.value = {};
  discountCode.value = '';
};

const hasDiscountApplied = () => !isEmpty(discountModel.value);

const clearUserMessages = () => {
  userMessage.value = '';
  userFriendlyError.value = '';
};
</script>

<template>
  <div>
    <form @submit="doSubmitCode">
      <div
        class="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3"
        v-if="!hasDiscountApplied()"
      >
        <div class="col-span-2">
          <div class="mt-1">
            <input
              type="text"
              :placeholder="$t('registerView.billing.discountPlaceholder')"
              id="discountCode"
              name="discountCode"
              v-model="discountCode"
              v-bind="discountCodeAttrs"
              required
              :disabled="isVerifyingKey"
              class="w-full input-transparent-text placeholder-gray-100"
            >
          </div>
        </div>

        <div>
          <div class="mt-1">
            <WithLoader :is-loading="isVerifyingKey">
              <button
                type="submit"
                class="w-full btn-cta"
              >
                {{ $t('registerView.billing.discountApply') }}
              </button>
            </WithLoader>
          </div>
        </div>
      </div>

      <div
        class="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3"
        v-else
      >
        <div class="col-span-2">
          <div class="mt-1 relative">
            <span class="absolute h-full ml-3 w-8 flex text-green-400">
              <CheckCircleIcon class="items-center justify-center align-middle" />
            </span>
            <input
              type="text"
              :placeholder="$t('registerView.billing.discountPlaceholder')"
              disabled
              id="discountCode"
              name="discountCode"
              v-model="discountCode"
              required
              class="pl-14 w-full input-transparent-text border-1 border-green-400"
            >
          </div>
        </div>

        <div>
          <div class="mt-1">
            <button
              type="button"
              @click="clearDiscount"
              class="w-full btn-cta"
            >
              {{ $t('registerView.billing.discountClear') }}
            </button>
          </div>
        </div>
      </div>
    </form>

    <InPlaceAlert
      class="mt-3"
      :message="userMessage"
      @close-alert="clearUserMessages"
    />
  </div>
</template>
